/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { type CardManagementContextValue } from "@farmlands/card-management";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import { selectRemoteConfig } from "../../../services/firebase/remote-config/remoteConfig.slice";
import { myFarmlandsApi } from "../../../services/myfarmlands-api";
import { error as logError } from "../../../util/log";
import { useActiveAccountQuery } from "../../account/hooks/useActiveAccountQuery";
import { useSelectedAccountQuery } from "../../account/hooks/useSelectedAccountQuery";
import { selectIdToken } from "../../azure-b2c/storage";

export type UseCardManagementContextValueResult = {
  /** Fully-built context value for `<CardManagementProvider value={...}>`. */
  contextValue: CardManagementContextValue;
  /** True while the underlying `/accounts` request is in flight. */
  isLoading: boolean;
  /** Error from the underlying `/accounts` request, if any. Wire to an `<AppError>` fallback. */
  error: unknown;
  /** Re-fires the `/accounts` request. Pass to `<AppError onTryAgainClick={...}>`. */
  refetch: () => void;
};

export const useCardManagementContextValue = (): UseCardManagementContextValueResult => {
  const { selectedAccount, accountId, isLoading, error, refetch } = useSelectedAccountQuery();
  const {
    contactUrl,
    permissionsChecksEnabled,
    farmlandsSecurityTipsUrl,
    farmlandsStoreLocatorUrl,
    farmlandsCardAppUrl,
    requestAdditionalCardUrl,
  } = useSelector(selectRemoteConfig);
  const idToken = useSelector(selectIdToken);

  const { isReady } = useActiveAccountQuery(selectedAccount?.accountNumber ?? null);

  const contextValue = useMemo<CardManagementContextValue>(
    () => ({
      apiClient: myFarmlandsApi,
      identity: {
        b2cAccountId: accountId ?? null,
        selectedAccountNumber: selectedAccount?.accountNumber ?? null,
        selectedContactPersonId: selectedAccount?.contactPersonId ?? null,
        isReady,
        idToken: idToken ?? null,
      },
      config: {
        farmlandsContactUrl: contactUrl,
        farmlandsContactPhoneNumber: "0800 200 600",
        farmlandsContactEmail: "ask@farmlands.co.nz",
        farmlandsSecurityTipsUrl,
        farmlandsStoreLocatorUrl,
        farmlandsCardAppUrl,
        requestAdditionalCardUrl,
        cardStatusPollingTimeoutMs: 60_000,
        cardStatusPollingIntervalMs: 5_000,
        permissionsApiBasePath: process.env.REACT_APP_PERMISSIONS_API_BASE_PATH ?? "",
        permissionsChecksEnabled,
      },
      onError: (err, errContext) => logError(err.message, errContext),
    }),
    [
      accountId,
      selectedAccount,
      isReady,
      contactUrl,
      idToken,
      permissionsChecksEnabled,
      farmlandsSecurityTipsUrl,
      farmlandsStoreLocatorUrl,
      farmlandsCardAppUrl,
      requestAdditionalCardUrl,
    ]
  );

  return { contextValue, isLoading, error, refetch };
};
